import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { Trash2, Plus, Filter, X, RefreshCw, Link2, ArrowUpDown, ArrowUp, ArrowDown, FileText } from "lucide-react";

import {
  TagAssignmentPopover,
  TagFilter,
  TagList,
  useEntityTags,
} from "@/components/TagComponents";
import {
  DocumentBadge,
  DocumentReferenceFields,
  useDocumentTypes,
} from "@/components/DocumentComponents";

import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  arListClients,
  arCreateClient,
  arListDailyReceipts,
  arCreateDailyReceipt,
  arUpdateDailyReceipt,
  arDeleteDailyReceipt,
  arDailyReceiptsSummary,
} from "@/lib/ar-api";
import { listPaymentMethods } from "@/lib/api";
import { newIdempotencyKey } from "@/lib/idempotency";
import { EUR, fmtDate, monthBounds, todayISO, yearBounds } from "@/lib/format";
import { SearchableCombobox } from "@/components/SearchableCombobox";

const INCOME_TYPE_OPTIONS = [
  { value: "invoiced", label: "Fatturato" },
  { value: "not_invoiced", label: "Non fatturato" },
  { value: "advance", label: "Anticipo" },
  { value: "refund", label: "Rimborso" },
  { value: "other", label: "Altro" },
];

const METHOD_OPTIONS = [
  { value: "bank_transfer", label: "Banca" },
  { value: "cash", label: "Contanti" },
  { value: "card", label: "POS" },
  { value: "sdd", label: "SDD" },
  { value: "check", label: "Assegno" },
  { value: "other", label: "Altro" },
];

const INCOME_TYPE_LABEL = Object.fromEntries(
  INCOME_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);
const METHOD_LABEL = Object.fromEntries(
  METHOD_OPTIONS.map((o) => [o.value, o.label]),
);

const incomeTypeBadgeClass = (t) => {
  switch (t) {
    case "not_invoiced":
      return "bg-amber-100 text-amber-800 border border-amber-300";
    case "advance":
      return "bg-blue-50 text-blue-700 border border-blue-200";
    case "refund":
      return "bg-rose-50 text-rose-700 border border-rose-200";
    case "other":
      return "bg-stone-100 text-stone-600 border border-stone-200";
    case "invoiced":
    default:
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  }
};

const blankDraft = () => ({
  receipt_date: todayISO(),
  client_id: "",
  client_free_name: "",
  description: "",
  amount: "",
  method: "bank_transfer",
  payment_method_id: "",
  income_type: "invoiced",
  notes: "",
});

const fmtAmount = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (Number.isNaN(n)) return "";
  return n.toFixed(2);
};

const parseAmount = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).replace(/\s/g, "").replace(",", ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
};

export default function DailyReceipts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedOccId = searchParams.get("occ") || "";
  const [defaultFrom, defaultTo] = useMemo(() => yearBounds(new Date()), []);
  const [filters, setFilters] = useState({
    date_from: defaultFrom,
    date_to: defaultTo,
    client_id: "all",
    income_type: "all",
    method: "all",
    q: "",
  });

  const [clients, setClients] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(blankDraft());
  const [savingDraft, setSavingDraft] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // v1.60.4 — AlertDialog conferma eliminazione (sostituisce window.confirm).
  // Contiene il record da eliminare così la maschera mostra dettagli
  // completi (data, cliente, importo, riferimento documento).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // v1.60.3: chiave idempotenza per Dialog "Nuovo incasso". Generata
  // all'apertura, invariata attraverso i retry, rigenerata dopo un
  // salvataggio riuscito e alla chiusura del dialog.
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());
  // Column sort: { key, direction }. Default = receipt_date desc.
  const [sort, setSort] = useState({ key: "receipt_date", direction: "desc" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: linkedOccId ? undefined : (filters.date_from || undefined),
        date_to: linkedOccId ? undefined : (filters.date_to || undefined),
        client_id: filters.client_id !== "all" ? filters.client_id : undefined,
        income_type:
          filters.income_type !== "all" ? filters.income_type : undefined,
        method: filters.method !== "all" ? filters.method : undefined,
        q: filters.q?.trim() ? filters.q.trim() : undefined,
        linked_occurrence_id: linkedOccId || undefined,
      };
      const [list, sum] = await Promise.all([
        arListDailyReceipts(params),
        arDailyReceiptsSummary({
          date_from: params.date_from,
          date_to: params.date_to,
        }),
      ]);
      setRows(list);
      setSummary(sum);
    } catch (err) {
      toast.error("Errore nel caricamento incassi giornalieri");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, linkedOccId]);

  useEffect(() => {
    (async () => {
      try {
        const list = await arListClients({ is_active: true });
        setClients(list);
      } catch (err) {
        console.error("Errore caricamento clienti", err);
      }
      try {
        // v1.60: metodi di pagamento centralizzati (Impostazioni → Personalizzazioni).
        // include_archived: mostriamo anche i metodi archiviati per rispettare
        // fallback storici; li disabiliteremo nel dropdown "nuovo".
        const pms = await listPaymentMethods({ includeArchived: true });
        setPaymentMethods(pms || []);
      } catch (err) {
        console.error("Errore caricamento metodi di pagamento", err);
      }
    })();
  }, []);

  // v1.60: helper per etichetta metodo. Preferisce l'anagrafica
  // PaymentMethodDef (via payment_method_id); fallback al valore enum storico.
  const methodLabelFor = useCallback(
    (row) => {
      if (row?.payment_method_id) {
        const pm = paymentMethods.find((p) => p.id === row.payment_method_id);
        if (pm) return pm.name;
      }
      return METHOD_LABEL[row?.method] || row?.method || "—";
    },
    [paymentMethods],
  );

  const handleCreateClient = useCallback(async (name) => {
    try {
      const created = await arCreateClient({ name });
      setClients((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      toast.success(`Cliente "${name}" creato`);
      return created;
    } catch (e) {
      toast.error(formatApiError(e, "Errore creazione cliente"));
      return null;
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const clientLabel = useCallback(
    (row) => {
      if (row.client_id) {
        const c = clients.find((cl) => cl.id === row.client_id);
        if (c) return c.name;
      }
      return row.client_free_name || "—";
    },
    [clients],
  );

  /* --- Column sorting ----------------------------------------------- */
  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  // Etichette (Fase 2): filtro + batch fetch dei tag sui daily receipts.
  // entity_type dedicato "daily_receipt" — non condiviso con altre entità
  // (i daily receipts sono voci autonome della Prima Nota Incassi).
  const [tagFilter, setTagFilter] = useState({ selectedIds: [], mode: "or", untaggedOnly: false });
  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const { tagsByEntity, refetch: refetchTags } = useEntityTags(
    "daily_receipt",
    rowIds,
  );

  // v1.59.1 ck14.2.b: gestione modal Riferimento Documento per DailyReceipt
  const documentTypes = useDocumentTypes(true);
  const documentTypesById = useMemo(() => {
    const m = {};
    for (const t of documentTypes) m[t.id] = t;
    return m;
  }, [documentTypes]);
  const [docEditRow, setDocEditRow] = useState(null);
  const [docEditValue, setDocEditValue] = useState({});
  const openDocEdit = (row) => {
    setDocEditRow(row);
    setDocEditValue({
      doc_type_id: row.doc_type_id || null,
      doc_number: row.doc_number || "",
      doc_date: row.doc_date || "",
      doc_protocol: row.doc_protocol || "",
      doc_notes: row.doc_notes || "",
    });
  };
  const closeDocEdit = () => setDocEditRow(null);
  const submitDocEdit = async () => {
    if (!docEditRow) return;
    try {
      const updated = await arUpdateDailyReceipt(docEditRow.id, {
        doc_type_id: docEditValue.doc_type_id || null,
        doc_number: docEditValue.doc_number || null,
        doc_date: docEditValue.doc_date || null,
        doc_protocol: docEditValue.doc_protocol || null,
        doc_notes: docEditValue.doc_notes || null,
      });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      toast.success("Riferimento documento aggiornato");
      closeDocEdit();
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio riferimento documento"));
    }
  };

  const sortedRows = useMemo(() => {
    const { key, direction } = sort;
    const dir = direction === "asc" ? 1 : -1;
    const getter = (r) => {
      if (key === "receipt_date") return r.receipt_date || "";
      if (key === "amount") return parseFloat(r.amount) || 0;
      if (key === "income_type") return r.income_type || "";
      if (key === "description") return (r.description || "").toLowerCase();
      if (key === "client")
        return (clientLabel(r) || "").toLowerCase();
      return "";
    };
    return [...rows]
      .filter((r) => {
        if (tagFilter.untaggedOnly) {
          return (tagsByEntity[r.id] || []).length === 0;
        }
        if (tagFilter.selectedIds.length === 0) return true;
        const rowTagIds = new Set((tagsByEntity[r.id] || []).map((t) => t.id));
        if (tagFilter.mode === "and") {
          return tagFilter.selectedIds.every((id) => rowTagIds.has(id));
        }
        return tagFilter.selectedIds.some((id) => rowTagIds.has(id));
      })
      .sort((a, b) => {
        const va = getter(a);
        const vb = getter(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
  }, [rows, sort, clientLabel, tagFilter, tagsByEntity]);

  // ---- Inline edit ----
  const handleCellPatch = async (row, field, rawValue) => {
    let value = rawValue;
    if (field === "amount") value = parseAmount(rawValue);
    if (field === "client_id" && value === "__free__") value = null;
    if (typeof value === "string" && value === "") {
      // Optional fields: store null instead of empty string.
      if (
        field === "client_id" ||
        field === "client_free_name" ||
        field === "description" ||
        field === "notes"
      ) {
        value = null;
      }
    }
    const prev = row[field];
    if (prev === value) return; // no-op
    // Optimistic
    setRows((rs) =>
      rs.map((r) => (r.id === row.id ? { ...r, [field]: value } : r)),
    );
    try {
      const updated = await arUpdateDailyReceipt(row.id, { [field]: value });
      setRows((rs) => rs.map((r) => (r.id === row.id ? updated : r)));
      // Refresh totals if amount/type changed
      if (field === "amount" || field === "income_type") {
        const sum = await arDailyReceiptsSummary({
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
        });
        setSummary(sum);
      }
    } catch (err) {
      toast.error("Modifica non riuscita");
      console.error(err);
      // Rollback
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, [field]: prev } : r)));
    }
  };

  const handleDelete = (row) => {
    // v1.60.4: apre AlertDialog con dettagli completi invece di window.confirm.
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await arDeleteDailyReceipt(deleteTarget.id);
      // Aggiornamento in-place: la Prima Nota è una vista derivata, quindi
      // rimuovendo la riga sparisce automaticamente anche da lì al prossimo
      // fetch. Qui basta togliere la riga dallo state locale.
      setRows((rs) => rs.filter((r) => r.id !== deleteTarget.id));
      toast.success("Incasso ordinario eliminato");
      setDeleteTarget(null);
      // Refetch summary/KPI: `amount`+`income_type` incidono sui totali.
      fetchAll();
    } catch (err) {
      const msg = formatApiError(err, "Eliminazione non riuscita");
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const submitDraft = async () => {
    if (!draft.receipt_date) {
      toast.error("Indica la data");
      return;
    }
    const amount = parseAmount(draft.amount);
    if (amount === null || amount < 0) {
      toast.error("Importo non valido");
      return;
    }
    setSavingDraft(true);
    try {
      const payload = {
        receipt_date: draft.receipt_date,
        client_id: draft.client_id ? draft.client_id : null,
        client_free_name: draft.client_id ? null : draft.client_free_name || null,
        description: draft.description || null,
        amount,
        // v1.60: payment_method_id è la fonte primaria; l'enum `method`
        // viene ricalcolato lato backend dal PaymentMethodDef selezionato
        // (best-effort, per Prima Nota unificata).
        payment_method_id: draft.payment_method_id || null,
        method: draft.method,
        income_type: draft.income_type,
        notes: draft.notes || null,
      };
      // v1.60.3: passa la Idempotency-Key. La stessa chiave viene mantenuta
      // se il salvataggio fallisce e l'utente ritenta; viene rigenerata solo
      // al successo o alla riapertura del dialog.
      await arCreateDailyReceipt(payload, { idempotencyKey });
      // Smart hint: if the saved date falls outside the current filter
      // range, the new row will NOT show in the table — let the user know
      // and offer a one-click "show all dates" reset.
      const inRange =
        (!filters.date_from || draft.receipt_date >= filters.date_from) &&
        (!filters.date_to || draft.receipt_date <= filters.date_to);
      if (!inRange) {
        toast.warning(
          `Incasso salvato (${draft.receipt_date}) ma fuori dal periodo filtrato. Allarga i filtri per vederlo.`,
          {
            duration: 7000,
            action: {
              label: "Vedi tutti",
              onClick: () =>
                setFilters((f) => ({ ...f, date_from: "", date_to: "" })),
            },
          },
        );
      } else {
        toast.success("Incasso registrato");
      }
      // v1.60: chiudi Dialog e resetta draft (mantieni data + metodo + tipo
      // per accelerare inserimenti a batch).
      setDialogOpen(false);
      // v1.60.3: consuma la chiave usata e generane una nuova per la
      // prossima operazione (secondo incasso batch = nuova chiave).
      setIdempotencyKey(newIdempotencyKey());
      setDraft({
        ...blankDraft(),
        receipt_date: draft.receipt_date,
        method: draft.method,
        payment_method_id: draft.payment_method_id,
        income_type: draft.income_type,
      });
      fetchAll();
    } catch (err) {
      if (err?.isIdempotencyConflict) {
        // v1.60.3: 409 Conflict — stessa chiave con payload diverso.
        // Non ritentare automaticamente; richiedi azione esplicita.
        toast.error(err.message);
      } else {
        toast.error("Registrazione non riuscita");
        console.error(err);
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const filtersActive =
    filters.date_from !== defaultFrom ||
    filters.date_to !== defaultTo ||
    filters.client_id !== "all" ||
    filters.income_type !== "all" ||
    filters.method !== "all" ||
    !!filters.q;

  const resetFilters = () =>
    setFilters({
      date_from: defaultFrom,
      date_to: defaultTo,
      client_id: "all",
      income_type: "all",
      method: "all",
      q: "",
    });

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "month") {
      const [f, t] = monthBounds(now);
      setFilters((s) => ({ ...s, date_from: f, date_to: t }));
    } else if (preset === "year") {
      const [f, t] = yearBounds(now);
      setFilters((s) => ({ ...s, date_from: f, date_to: t }));
    } else if (preset === "all") {
      setFilters((s) => ({ ...s, date_from: "", date_to: "" }));
    }
  };

  return (
    <div data-testid="daily-receipts-page" className="space-y-6">
      <PageHeader
        testId="daily-receipts-header"
        title="Incassi ordinari"
        subtitle="Prima nota incassi — registrazione rapida degli incassi non ricorrenti."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="daily-receipts-refresh"
              variant="outline"
              size="sm"
              onClick={fetchAll}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Aggiorna
            </Button>
            <Button
              data-testid="btn-new-daily-receipt"
              size="sm"
              onClick={() => {
                setDraft(blankDraft());
                // v1.60.3: nuova operazione = nuova chiave idempotenza.
                setIdempotencyKey(newIdempotencyKey());
                setDialogOpen(true);
              }}
            >
              <Plus size={14} className="mr-1" />
              Nuovo incasso
            </Button>
          </div>
        }
      />

      {linkedOccId && (
        <div
          data-testid="linked-occ-banner"
          className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900"
        >
          <Link2 size={16} />
          <span className="flex-1">
            Filtrato per una <strong>scadenza incasso</strong> specifica · solo
            righe di Prima Nota collegate.
          </span>
          <Link
            to="/receivables"
            className="text-xs underline hover:text-emerald-700"
          >
            ← Torna a Scadenze incassi
          </Link>
          <button
            type="button"
            data-testid="linked-occ-banner-clear"
            onClick={() => {
              setSearchParams({});
            }}
            className="rounded-full bg-white px-2 py-0.5 text-xs text-emerald-700 hover:bg-emerald-100"
          >
            Rimuovi filtro
          </button>
        </div>
      )}

      {/* Filters */}
      <section
        data-testid="daily-receipts-filters"
        className="rounded-lg border border-stone-200 bg-card p-4"
      >
        <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-stone-500">
          <Filter size={12} /> Filtri
          <PeriodPicker
            value={{ from: filters.date_from, to: filters.date_to }}
            onChange={(from, to) =>
              setFilters((s) => ({ ...s, date_from: from, date_to: to }))
            }
            testId="dr-period"
          />
          <div className="ml-1 flex gap-1">
            <button
              type="button"
              data-testid="preset-month"
              onClick={() => applyPreset("month")}
              className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-normal text-stone-600 hover:bg-stone-200"
            >
              Mese
            </button>
            <button
              type="button"
              data-testid="preset-year"
              onClick={() => applyPreset("year")}
              className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-normal text-stone-600 hover:bg-stone-200"
            >
              Anno
            </button>
            <button
              type="button"
              data-testid="preset-all"
              onClick={() => applyPreset("all")}
              className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-normal text-stone-600 hover:bg-stone-200"
            >
              Tutti
            </button>
          </div>
          {filtersActive && (
            <button
              type="button"
              onClick={resetFilters}
              data-testid="daily-receipts-filters-reset"
              className="ml-auto inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-600 hover:bg-stone-200"
            >
              <X size={10} /> Azzera
            </button>
          )}
          <div className={filtersActive ? "" : "ml-auto"}>
            <TagFilter
              selectedIds={tagFilter.selectedIds}
              mode={tagFilter.mode}
              untaggedOnly={tagFilter.untaggedOnly}
              onChange={setTagFilter}
              testIdPrefix="daily-receipts-tag-filter"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <div>
            <label className="text-[11px] text-stone-500">Da</label>
            <Input
              data-testid="filter-date-from"
              type="date"
              value={filters.date_from}
              onChange={(e) =>
                setFilters({ ...filters, date_from: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div>
            <label className="text-[11px] text-stone-500">A</label>
            <Input
              data-testid="filter-date-to"
              type="date"
              value={filters.date_to}
              onChange={(e) =>
                setFilters({ ...filters, date_to: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div>
            <label className="text-[11px] text-stone-500">Cliente</label>
            <Select
              value={filters.client_id}
              onValueChange={(v) => setFilters({ ...filters, client_id: v })}
            >
              <SelectTrigger
                data-testid="filter-client"
                className="h-9"
              >
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-stone-500">Tipo incasso</label>
            <Select
              value={filters.income_type}
              onValueChange={(v) =>
                setFilters({ ...filters, income_type: v })
              }
            >
              <SelectTrigger
                data-testid="filter-income-type"
                className="h-9"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {INCOME_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-stone-500">Metodo</label>
            <Select
              value={filters.method}
              onValueChange={(v) => setFilters({ ...filters, method: v })}
            >
              <SelectTrigger
                data-testid="filter-method"
                className="h-9"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {METHOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-stone-500">Cerca</label>
            <Input
              data-testid="filter-search"
              type="text"
              placeholder="Descrizione, cliente…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="h-9"
            />
          </div>
        </div>
      </section>

      {/* Totals strip — clickable KPI cards: filter by income_type */}
      <section
        data-testid="daily-receipts-totals"
        className="grid grid-cols-2 gap-3 md:grid-cols-6"
      >
        <TotalCard
          testId="total-all"
          label="Totale periodo"
          value={summary?.total_amount}
          count={summary?.total_count}
          highlight
          active={filters.income_type === "all"}
          onClick={() => setFilters((f) => ({ ...f, income_type: "all" }))}
        />
        {INCOME_TYPE_OPTIONS.map((opt) => (
          <TotalCard
            key={opt.value}
            testId={`total-${opt.value}`}
            label={opt.label}
            value={summary?.by_type?.[opt.value]?.total}
            count={summary?.by_type?.[opt.value]?.count}
            warning={opt.value === "not_invoiced"}
            active={filters.income_type === opt.value}
            onClick={() =>
              setFilters((f) => ({
                ...f,
                income_type:
                  f.income_type === opt.value ? "all" : opt.value,
              }))
            }
          />
        ))}
      </section>

      {/* v1.60: form "Nuovo incasso" rimosso dalla pagina.
          Il form è ora esposto tramite Dialog (in fondo al render). */}

      {/* Ledger table — registered receipts only */}
      <section
        data-testid="daily-receipts-table-wrapper"
        className="overflow-x-auto rounded-lg border border-stone-200 bg-card"
      >
        <table className="min-w-[1280px] w-full border-collapse text-sm">
          <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <SortableTh
                className="w-[120px]"
                sortKey="receipt_date"
                sort={sort}
                onSort={toggleSort}
                testId="sort-date"
              >
                Data
              </SortableTh>
              <SortableTh
                className="w-[200px]"
                sortKey="client"
                sort={sort}
                onSort={toggleSort}
                testId="sort-client"
              >
                Cliente
              </SortableTh>
              <SortableTh
                sortKey="description"
                sort={sort}
                onSort={toggleSort}
                testId="sort-description"
              >
                Descrizione
              </SortableTh>
              <SortableTh
                className="w-[120px] text-right"
                sortKey="amount"
                sort={sort}
                onSort={toggleSort}
                testId="sort-amount"
                align="right"
              >
                Importo
              </SortableTh>
              <Th className="w-[120px]">Metodo</Th>
              <SortableTh
                className="w-[140px]"
                sortKey="income_type"
                sort={sort}
                onSort={toggleSort}
                testId="sort-type"
              >
                Tipo
              </SortableTh>
              <Th>Note</Th>
              <Th className="w-[60px]" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  data-testid="daily-receipts-empty"
                  colSpan={8}
                  className="px-4 py-10 text-center text-sm text-stone-400"
                >
                  Nessun incasso nel periodo. Usa il pulsante &ldquo;+ Nuovo incasso&rdquo; in alto per registrarne uno.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <LedgerRow
                  key={row.id}
                  row={row}
                  clients={clients}
                  clientLabel={clientLabel(row)}
                  onCreateClient={handleCreateClient}
                  onPatch={handleCellPatch}
                  onDelete={handleDelete}
                  tags={tagsByEntity[row.id] || []}
                  onTagsChange={refetchTags}
                  documentTypesById={documentTypesById}
                  onEditDoc={openDocEdit}
                  paymentMethods={paymentMethods}
                  methodLabelFor={methodLabelFor}
                />
              ))
            )}
          </tbody>
          {sortedRows.length > 0 && (
            <tfoot className="bg-stone-50 text-sm font-semibold">
              <tr>
                <td colSpan={3} className="px-3 py-3 text-right text-stone-500">
                  Totale righe filtrate ({sortedRows.length})
                </td>
                <td
                  data-testid="footer-total-amount"
                  className="px-3 py-3 text-right tabular-nums text-stone-900"
                >
                  {EUR(sortedRows.reduce((s, r) => s + parseFloat(r.amount || 0), 0))}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </section>

      {/* v1.60: Dialog "Nuovo incasso" ─ sostituisce il form inline.
          Focus-flow tastiera: Data → Cliente (Enter seleziona/crea + focus next)
          → Descrizione → Importo → Metodo → Tipo → Note → [Aggiungi]. */}
      <NewDailyReceiptDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          // v1.60.3: chiusura senza salvare = rigenera chiave alla prossima
          // apertura (mantenendola invariata durante la sessione corrente).
          if (!open) {
            setIdempotencyKey(newIdempotencyKey());
          }
        }}
        draft={draft}
        setDraft={setDraft}
        clients={clients}
        onCreateClient={handleCreateClient}
        paymentMethods={paymentMethods}
        onSubmit={submitDraft}
        saving={savingDraft}
      />

      {/* v1.59.1 ck14.2.b: Modal Riferimento Documento per DailyReceipt */}
      <Dialog open={!!docEditRow} onOpenChange={(o) => { if (!o) closeDocEdit(); }}>
        <DialogContent data-testid="dialog-daily-receipt-docref">
          <DialogHeader>
            <DialogTitle>Riferimento documento</DialogTitle>
            <DialogDescription>
              {docEditRow?.description
                ? `Incasso: ${docEditRow.description}`
                : "Aggiungi o modifica i dati del documento allegato all'incasso."}
            </DialogDescription>
          </DialogHeader>
          {docEditRow && (
            <DocumentReferenceFields
              value={docEditValue}
              onChange={setDocEditValue}
              entityType="daily_receipt"
              entityId={docEditRow.id}
              defaultOpen={true}
              testIdPrefix="daily-docref"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDocEdit}>
              Annulla
            </Button>
            <Button data-testid="btn-submit-daily-docref" onClick={submitDocEdit}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.60.4 — Conferma eliminazione incasso ordinario (AlertDialog).
          Mostra dettagli utili (data, cliente, importo, riferimento
          documento) così l'utente conferma consapevolmente. La riga
          verrà rimossa anche dalla Prima Nota (vista derivata). */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}
      >
        <AlertDialogContent data-testid="dialog-delete-daily-receipt">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'incasso ordinario?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  L'operazione verrà rimossa <strong>anche dalla Prima Nota</strong>{" "}
                  (i movimenti sono derivati automaticamente dagli incassi
                  registrati). L'azione non è annullabile.
                </p>
                {deleteTarget && (
                  <div className="rounded-md border border-stone-200 bg-stone-50 p-2.5 text-[12px] leading-relaxed text-stone-700">
                    <div>
                      <span className="text-stone-500">Data: </span>
                      <span className="font-medium">{fmtDate(deleteTarget.receipt_date)}</span>
                    </div>
                    <div>
                      <span className="text-stone-500">Cliente: </span>
                      <span className="font-medium">{clientLabel(deleteTarget)}</span>
                    </div>
                    <div>
                      <span className="text-stone-500">Importo: </span>
                      <span className="font-mono font-semibold tabular-nums text-stone-900">
                        {EUR(deleteTarget.amount)}
                      </span>
                    </div>
                    {deleteTarget.description && (
                      <div className="mt-1">
                        <span className="text-stone-500">Descrizione: </span>
                        <span>{deleteTarget.description}</span>
                      </div>
                    )}
                    {(deleteTarget.doc_number || deleteTarget.doc_protocol) && (
                      <div className="mt-1 text-[11px] text-stone-500">
                        Riferimento documento:{" "}
                        {deleteTarget.doc_number && (
                          <span className="font-mono">#{deleteTarget.doc_number}</span>
                        )}
                        {deleteTarget.doc_protocol && (
                          <span className="ml-2 font-mono">
                            prot. {deleteTarget.doc_protocol}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="btn-cancel-delete-daily-receipt"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="btn-confirm-delete-daily-receipt"
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {deleting ? "Eliminazione…" : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th
    className={`border-b border-stone-200 px-3 py-2 text-left font-medium ${className}`}
  >
    {children}
  </th>
);

const SortableTh = ({ children, className = "", sortKey, sort, onSort, testId, align = "left" }) => {
  const isActive = sort.key === sortKey;
  const Icon = isActive
    ? sort.direction === "asc"
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;
  return (
    <th
      className={`border-b border-stone-200 px-3 py-2 font-medium select-none ${align === "right" ? "text-right" : "text-left"} ${className}`}
    >
      <button
        type="button"
        data-testid={testId}
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 ${align === "right" ? "ml-auto justify-end" : ""} ${isActive ? "text-stone-900" : "text-stone-500"} hover:text-stone-900`}
      >
        {children}
        <Icon size={11} className={isActive ? "" : "opacity-40"} />
      </button>
    </th>
  );
};

const Td = ({ children, className = "" }) => (
  <td className={`border-b border-stone-100 px-2 py-1.5 align-middle ${className}`}>
    {children}
  </td>
);

const TotalCard = ({ label, value, count, highlight, warning, testId, onClick, active }) => {
  const base = onClick
    ? "cursor-pointer transition hover:shadow-sm hover:-translate-y-px"
    : "";
  const borderTone = active
    ? "border-primary ring-1 ring-primary/30 bg-primary/5"
    : highlight
      ? "border-primary/40 bg-primary/5"
      : warning
        ? "border-amber-300 bg-amber-50/50"
        : "border-stone-200 bg-card";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-lg border px-4 py-3 text-left ${base} ${borderTone} disabled:cursor-default`}
    >
      <div className="text-[10px] font-medium uppercase tracking-wider text-stone-500">
        {label}
        {active && (
          <span
            data-testid={`${testId}-active`}
            className="ml-1.5 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold text-primary"
          >
            filtro attivo
          </span>
        )}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-stone-900">
        {EUR(value || 0)}
      </div>
      <div className="text-[10px] text-stone-400">{count || 0} righe</div>
    </button>
  );
};

function LedgerRow({ row, clients, clientLabel, onCreateClient, onPatch, onDelete, tags = [], onTagsChange, documentTypesById = {}, onEditDoc, paymentMethods = [], methodLabelFor }) {
  // Local controlled state per row for inline editing
  const [local, setLocal] = useState({
    receipt_date: row.receipt_date,
    description: row.description || "",
    amount: fmtAmount(row.amount),
    notes: row.notes || "",
    client_free_name: row.client_free_name || "",
  });

  useEffect(() => {
    setLocal({
      receipt_date: row.receipt_date,
      description: row.description || "",
      amount: fmtAmount(row.amount),
      notes: row.notes || "",
      client_free_name: row.client_free_name || "",
    });
  }, [row]);

  const commit = (field, value) => {
    if (value === (row[field] ?? "")) return;
    onPatch(row, field, value);
  };

  return (
    <tr
      data-testid={`row-${row.id}`}
      className="border-t border-stone-100 hover:bg-stone-50/60"
    >
      <Td>
        <Input
          data-testid={`row-date-${row.id}`}
          type="date"
          value={local.receipt_date}
          onChange={(e) => setLocal({ ...local, receipt_date: e.target.value })}
          onBlur={(e) => commit("receipt_date", e.target.value)}
          className="h-8 border-0 bg-transparent p-1 focus-visible:ring-1"
        />
      </Td>
      <Td>
        <SearchableCombobox
          testId={`row-client-${row.id}`}
          value={row.client_id || ""}
          onChange={(id) => {
            if (!id) {
              onPatch(row, "client_id", null);
            } else {
              onPatch(row, "client_id", id);
              if (row.client_free_name) {
                onPatch(row, "client_free_name", null);
              }
            }
          }}
          options={clients}
          onCreate={async (name) => {
            const c = await onCreateClient(name);
            return c;
          }}
          placeholder={clientLabel || "Seleziona cliente…"}
          createLabel="Crea nuovo cliente:"
          emptyLabel="Nessun cliente trovato"
          className="h-8 border-0 bg-transparent p-1 text-xs focus:ring-1"
        />
        {!row.client_id && row.client_free_name && (
          <p className="px-1 text-[10px] italic text-stone-400">
            (Cliente libero: {row.client_free_name})
          </p>
        )}
      </Td>
      <Td>
        <Input
          data-testid={`row-description-${row.id}`}
          type="text"
          value={local.description}
          onChange={(e) => setLocal({ ...local, description: e.target.value })}
          onBlur={(e) => commit("description", e.target.value)}
          className="h-8 border-0 bg-transparent p-1 focus-visible:ring-1"
        />
        <div className="mt-0.5 flex flex-wrap items-center gap-1 px-1">
          <TagList
            tags={tags}
            size="sm"
            max={4}
            onRemove={async (t) => {
              try {
                await (await import("@/lib/api")).detachTag(
                  t.id,
                  "daily_receipt",
                  row.id,
                );
                onTagsChange?.();
              } catch {
                /* silent */
              }
            }}
          />
          <TagAssignmentPopover
            entityType="daily_receipt"
            entityId={row.id}
            currentTagIds={tags.map((t) => t.id)}
            onChange={() => onTagsChange?.()}
            testIdPrefix={`daily-receipt-tag-${row.id}`}
          />
          {(row.doc_type_id || row.doc_number) ? (
            <button
              type="button"
              onClick={() => onEditDoc?.(row)}
              className="inline-flex items-center hover:opacity-80"
              data-testid={`daily-receipt-doc-badge-${row.id}`}
              title="Modifica riferimento documento"
            >
              <DocumentBadge
                type={documentTypesById[row.doc_type_id]}
                number={row.doc_number}
                date={row.doc_date}
              />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onEditDoc?.(row)}
              className="inline-flex items-center gap-1 rounded border border-dashed border-stone-300 bg-white px-1.5 py-0.5 text-[10px] text-stone-500 hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
              data-testid={`daily-receipt-doc-add-${row.id}`}
              title="Aggiungi riferimento documento"
            >
              <FileText size={9} />
              Documento
            </button>
          )}
        </div>
      </Td>
      <Td className="text-right">
        <Input
          data-testid={`row-amount-${row.id}`}
          type="text"
          inputMode="decimal"
          value={local.amount}
          onChange={(e) => setLocal({ ...local, amount: e.target.value })}
          onBlur={(e) => {
            const n = parseAmount(e.target.value);
            if (n === null) {
              setLocal({ ...local, amount: fmtAmount(row.amount) });
              return;
            }
            commit("amount", n);
          }}
          className="h-8 border-0 bg-transparent p-1 text-right tabular-nums focus-visible:ring-1"
        />
      </Td>
      <Td>
        {/* v1.60: metodo di pagamento centralizzato.
            - Attivi: selezionabili.
            - Archiviati: mostrati (disabilitati) SOLO se referenziano la riga corrente
              (fallback storico); non li offriamo per un nuovo cambio di metodo.
            - Fallback: se payment_method_id è NULL o punta a metodo assente,
              mostra la label legacy derivata dall'enum `method`. */}
        <Select
          value={row.payment_method_id || `__legacy__${row.method}`}
          onValueChange={(v) => {
            if (v.startsWith("__legacy__")) return; // no-op: sentinella di fallback
            onPatch(row, "payment_method_id", v);
          }}
        >
          <SelectTrigger
            data-testid={`row-method-${row.id}`}
            className="h-7 border-0 bg-transparent p-1 text-xs focus:ring-1"
          >
            <SelectValue>{methodLabelFor ? methodLabelFor(row) : (METHOD_LABEL[row.method] || row.method)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(() => {
              const active = paymentMethods.filter((m) => m.is_active !== false);
              const archived = paymentMethods.filter((m) => m.is_active === false);
              const currentIsArchived = row.payment_method_id
                && archived.some((m) => m.id === row.payment_method_id);
              return (
                <>
                  {active.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                  {currentIsArchived && (
                    <SelectItem
                      key={row.payment_method_id}
                      value={row.payment_method_id}
                      disabled
                    >
                      {archived.find((m) => m.id === row.payment_method_id)?.name}
                      <span className="ml-1 text-[9px] text-stone-400">(archiviato)</span>
                    </SelectItem>
                  )}
                  {!row.payment_method_id && (
                    <SelectItem
                      value={`__legacy__${row.method}`}
                      disabled
                    >
                      {METHOD_LABEL[row.method] || row.method}
                      <span className="ml-1 text-[9px] text-stone-400">(legacy)</span>
                    </SelectItem>
                  )}
                </>
              );
            })()}
          </SelectContent>
        </Select>
      </Td>
      <Td>
        <Select
          value={row.income_type}
          onValueChange={(v) => onPatch(row, "income_type", v)}
        >
          <SelectTrigger
            data-testid={`row-income-type-${row.id}`}
            className="h-7 border-0 bg-transparent p-1 text-xs focus:ring-1"
          >
            <SelectValue>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${incomeTypeBadgeClass(row.income_type)}`}
              >
                {row.income_type === "not_invoiced" && (
                  <span className="font-bold">N</span>
                )}
                {INCOME_TYPE_LABEL[row.income_type] || row.income_type}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {INCOME_TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Td>
      <Td>
        <Input
          data-testid={`row-notes-${row.id}`}
          type="text"
          value={local.notes}
          onChange={(e) => setLocal({ ...local, notes: e.target.value })}
          onBlur={(e) => commit("notes", e.target.value)}
          className="h-8 border-0 bg-transparent p-1 focus-visible:ring-1"
        />
      </Td>
      <Td>
        <Button
          data-testid={`row-delete-${row.id}`}
          size="icon"
          variant="ghost"
          onClick={() => onDelete(row)}
          className="h-7 w-7 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
          title="Elimina incasso ordinario"
        >
          <Trash2 size={14} />
        </Button>
      </Td>
    </tr>
  );
}

/**
 * v1.60: Dialog "Nuovo incasso" per Incassi ordinari.
 * Sostituisce il form inline sempre visibile. Layout compatto in 12 colonne.
 * Include navigazione da tastiera (Cliente → focus Descrizione al select).
 */
function NewDailyReceiptDialog({
  open,
  onOpenChange,
  draft,
  setDraft,
  clients,
  onCreateClient,
  paymentMethods,
  onSubmit,
  saving,
}) {
  // Refs per il focus-flow guidato da tastiera.
  const descriptionRef = useRef(null);
  const amountRef = useRef(null);

  // Dropdown metodi: mostra SEMPRE tutti (anche archiviati) per continuità
  // storica, ma disabilita la selezione dei metodi archiviati per i nuovi
  // inserimenti. L'ordinamento pone i metodi attivi in cima.
  const methodOptions = useMemo(() => {
    const active = paymentMethods.filter((m) => m.is_active !== false);
    const archived = paymentMethods.filter((m) => m.is_active === false);
    active.sort((a, b) => a.name.localeCompare(b.name, "it"));
    archived.sort((a, b) => a.name.localeCompare(b.name, "it"));
    return { active, archived };
  }, [paymentMethods]);

  // Enter sui campi non-textarea → submit (previene submit accidentale
  // altrove: il combobox ha già stopPropagation).
  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      // Se il focus è su input generico, richiama submit.
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="dialog-new-daily-receipt"
        className="max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle>Nuovo incasso</DialogTitle>
          <DialogDescription>
            Registra un incasso ordinario. Cliente e metodo attingono dalle
            anagrafiche condivise (Impostazioni).
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          onKeyDown={handleFormKeyDown}
          className="grid grid-cols-1 gap-3 md:grid-cols-12"
        >
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Data
            </label>
            <Input
              data-testid="dialog-new-row-date"
              type="date"
              value={draft.receipt_date}
              onChange={(e) => setDraft({ ...draft, receipt_date: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Cliente
            </label>
            <SearchableCombobox
              testId="dialog-new-row-client"
              value={draft.client_id}
              onChange={(id) =>
                setDraft({ ...draft, client_id: id || "", client_free_name: "" })
              }
              onSelectComplete={() => descriptionRef.current?.focus()}
              options={clients}
              onCreate={onCreateClient}
              placeholder="Seleziona cliente…"
              createLabel="Crea nuovo cliente:"
              emptyLabel="Nessun cliente trovato"
              className="h-9"
            />
          </div>
          <div className="md:col-span-4">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Importo (€)
            </label>
            <Input
              ref={amountRef}
              data-testid="dialog-new-row-amount"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="h-9 text-right font-mono tabular-nums"
            />
          </div>

          <div className="md:col-span-12">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Descrizione
            </label>
            <Input
              ref={descriptionRef}
              data-testid="dialog-new-row-description"
              type="text"
              placeholder="Descrizione"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              className="h-9"
            />
          </div>

          <div className="md:col-span-5">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Metodo di pagamento
            </label>
            <Select
              value={draft.payment_method_id || ""}
              onValueChange={(v) => setDraft({ ...draft, payment_method_id: v })}
            >
              <SelectTrigger data-testid="dialog-new-row-payment-method" className="h-9">
                <SelectValue placeholder="Seleziona metodo…" />
              </SelectTrigger>
              <SelectContent>
                {methodOptions.active.map((m) => (
                  <SelectItem
                    key={m.id}
                    value={m.id}
                    data-testid={`dialog-new-row-pm-${m.id}`}
                  >
                    {m.name}
                  </SelectItem>
                ))}
                {methodOptions.archived.length > 0 && (
                  <div className="border-t border-stone-200 px-2 py-1 text-[10px] uppercase tracking-wider text-stone-400">
                    Archiviati (non selezionabili)
                  </div>
                )}
                {methodOptions.archived.map((m) => (
                  <SelectItem
                    key={m.id}
                    value={m.id}
                    disabled
                    data-testid={`dialog-new-row-pm-${m.id}`}
                  >
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Tipo incasso
            </label>
            <Select
              value={draft.income_type}
              onValueChange={(v) => setDraft({ ...draft, income_type: v })}
            >
              <SelectTrigger data-testid="dialog-new-row-income-type" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INCOME_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-12">
            <label className="text-[10px] uppercase tracking-wider text-stone-500">
              Note (opzionali)
            </label>
            <Input
              data-testid="dialog-new-row-notes"
              type="text"
              placeholder="Note"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              className="h-9"
            />
          </div>
        </form>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="dialog-new-daily-receipt-cancel"
            onClick={() => onOpenChange(false)}
          >
            Annulla
          </Button>
          <Button
            data-testid="dialog-new-daily-receipt-save"
            onClick={onSubmit}
            disabled={saving}
          >
            <Plus size={14} className="mr-1" />
            {saving ? "Salvataggio…" : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
